import React, { useState, useEffect } from 'react';
import { configAPI } from '../../lib/api';
import { GlassCard } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Settings, Save } from 'lucide-react';

const AdminConfig = () => {
  const [config, setConfig] = useState({
    escalation_hours: 24,
    rank_criteria: {
      rm: 5,
      avp: 15,
      svp: 30,
      evp: 50,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await configAPI.getConfig();
      setConfig(res.data);
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await configAPI.updateConfig({
        escalation_hours: parseInt(config.escalation_hours),
        rank_criteria: Object.fromEntries(
          Object.entries(config.rank_criteria).map(([k, v]) => [k, parseInt(v)])
        ),
      });
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-config">
      <div>
        <h1 className="text-2xl font-bold text-white">System Configuration</h1>
        <p className="text-slate-400 mt-1">Configure system-wide settings</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Escalation Settings */}
        <GlassCard>
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Appointment Escalation</h2>
          </div>
          <div>
            <Label className="text-slate-300">Escalation Time (Hours)</Label>
            <Input
              type="number"
              value={config.escalation_hours}
              onChange={(e) => setConfig({ ...config, escalation_hours: e.target.value })}
              className="bg-slate-800/50 border-slate-700 text-white mt-2 max-w-xs"
              data-testid="escalation-hours"
            />
            <p className="text-xs text-slate-500 mt-2">
              Appointments not approved within this time will be escalated
            </p>
          </div>
        </GlassCard>

        {/* Rank Criteria */}
        <GlassCard>
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Auto-Ranking Criteria</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Relationship Manager (RM)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.rm || 5}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, rm: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Assistant Vice President (AVP)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.avp || 15}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, avp: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Senior Vice President (SVP)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.svp || 30}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, svp: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Executive Vice President (EVP)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.evp || 50}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, evp: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>

      <Button
        onClick={handleSave}
        disabled={saving}
        className="bg-emerald-500 hover:bg-emerald-600"
        data-testid="save-config"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Configuration'}
      </Button>
    </div>
  );
};

export default AdminConfig;
